import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../core/auth/auth.service';
import { LearningCenterService } from '../../../learning-center/engine/learning-center.service';
import { TourController } from '../../../learning-center/engine/tour-controller.service';
import {
  COMPANION,
  Point,
  SkipperPerch,
  clampDrag,
  resolveRelease,
  settledTopLeft,
} from '../../domain/skipper-perch';
import { SkipperCompanionController } from '../../engine/skipper-companion.controller';
import { SkipperCompanionStore } from '../../engine/skipper-companion.store';
import { SkipperScreenService } from '../../engine/skipper-screen.service';
import { SkipperSessionMemory } from '../../engine/skipper-session-memory.service';
import { SkipperStageService } from '../../engine/skipper-stage.service';
import { SkipperViewportService } from '../../engine/skipper-viewport.service';
import { SkipperAvatarComponent } from '../skipper-avatar/skipper-avatar.component';
import { SkipperSpeechBubbleComponent } from '../skipper-speech-bubble/skipper-speech-bubble.component';

/**
 * Short, warm, and never a promise Skipper cannot keep — which is why the
 * app's "Let's find some fish!" is not here: Skipper explains the app, it does
 * not find fish.
 */
const GREETINGS = ['Ahoy! Tap me for tips and tours.', 'Need a hand? Just tap me.', 'Tap me if you get stuck.'];

const GREETING_DELAY_MS = 7000;
const GREETING_HOLD_MS = 5200;
/** How far an arrow key moves Skipper up or down, as a fraction of the usable height. */
const KEY_STEP = 0.05;
/** Release velocity is measured over this much of the drag's tail. */
const VELOCITY_WINDOW_MS = 100;

/**
 * Skipper on every page: normally tucked into an edge with one wing peeking
 * out, draggable anywhere, tapped to come out. Port of
 * `SkipperFloatingCompanion`; mounted once in the app shell.
 *
 * ─── WHY DRAGGABLE AND NOT A FIXED FAB ────────────────────────────────────
 * A fixed position always covers something — the layer switcher on one page,
 * a vessel card on another. Being able to shove Skipper off the edge is the
 * "get out of my way" gesture; the peeking wing left behind is what keeps that
 * from feeling like Skipper was lost.
 *
 * ─── INPUT ────────────────────────────────────────────────────────────────
 * Pointer Events (mouse, touch, pen) with pointer capture. A press becomes a
 * drag only after 5px of travel, so a drag never also fires a tap. Moves are
 * handled outside Angular's zone and written straight to `transform` —
 * dragging never runs change detection over the 13k-line map page.
 * Keyboard: Enter/Space = tap; Up/Down move; Left/Right switch edge, or tuck
 * into / pull out of the current one.
 */
@Component({
  selector: 'app-skipper-floating-companion',
  standalone: true,
  imports: [SkipperAvatarComponent, SkipperSpeechBubbleComponent],
  templateUrl: './skipper-floating-companion.component.html',
  styleUrl: './skipper-floating-companion.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkipperFloatingCompanionComponent implements AfterViewInit, OnDestroy {
  private store = inject(SkipperCompanionStore);
  private controller = inject(SkipperCompanionController);
  private memory = inject(SkipperSessionMemory);
  private viewport = inject(SkipperViewportService);
  private stage = inject(SkipperStageService);
  private screen = inject(SkipperScreenService);
  private tours = inject(TourController);
  private learningCenter = inject(LearningCenterService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private zone = inject(NgZone);

  readonly c = COMPANION;
  readonly perch = this.store.perch;
  readonly bounds = this.viewport.bounds;
  readonly dragging = signal(false);
  readonly greeting = signal<string | null>(null);

  private readonly loggedIn = toSignal(this.auth.isLoggedIn$, {
    initialValue: this.auth.isAuthenticated(),
  });

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /**
   * On screen only for a signed-in user outside onboarding, and never over a
   * running tour or the Learning Center — a bird hopping about on top of a
   * spotlight fights the thing the user is being asked to look at.
   */
  readonly shown = computed(
    () =>
      environment.skipperEnabled &&
      this.loggedIn() &&
      !this.url().startsWith('/auth') &&
      this.controller.visible() &&
      this.tours.state() === null &&
      !this.learningCenter.isOpen(),
  );

  readonly tucked = computed(() => this.perch().tucked && !this.dragging());
  readonly onRight = computed(() => this.perch().edge === 'right');

  readonly settled = computed(() =>
    settledTopLeft({ ...this.perch(), tucked: this.tucked() }, this.bounds()),
  );

  readonly mood = computed(() =>
    this.tucked() ? 'peeking' : this.dragging() ? 'curious' : 'idle',
  );

  readonly label = computed(() =>
    this.tucked()
      ? 'Skipper is tucked away. Activate to bring Skipper back.'
      : 'Ask Skipper. Drag to move Skipper, or push Skipper into the edge to tuck Skipper away.',
  );

  /** Pulled back into the dock by 94% of Skipper's width; what is left peeks out. */
  readonly avatarOffset = -COMPANION.width * COMPANION.tuckInsetFraction;

  @ViewChild(SkipperAvatarComponent, { read: ElementRef }) private avatarEl?: ElementRef<HTMLElement>;

  private birdEl: HTMLElement | null = null;
  @ViewChild('bird') private set birdRef(ref: ElementRef<HTMLElement> | undefined) {
    this.birdEl = ref?.nativeElement ?? null;
    if (!this.birdEl) return;
    this.place(this.settled(), false);
    // Back from the stage: focus returns to the bird that opened it.
    if (this.focusOnReturn) {
      this.focusOnReturn = false;
      this.birdEl.focus({ preventScroll: true });
    }
  }

  private focusOnReturn = false;

  private greetingTimer: ReturnType<typeof setTimeout> | undefined;
  private greetingArmed = false;
  private releaseDrag: (() => void) | null = null;

  constructor() {
    // Settled position follows the perch and the viewport. Never while a drag
    // owns the transform.
    effect(() => {
      const at = this.settled();
      if (!this.dragging()) this.place(at, true);
    });

    // Says hello once a session, a few seconds after Skipper is out — never
    // while tucked, there is nothing to speak out of down there.
    effect(() => {
      if (this.shown() && !this.perch().tucked) this.armGreeting();
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.remeasureNextFrame());
  }

  ngAfterViewInit(): void {
    this.remeasureNextFrame();
  }

  ngOnDestroy(): void {
    clearTimeout(this.greetingTimer);
    this.releaseDrag?.();
  }

  // ── Tap ─────────────────────────────────────────────────────────────────────

  onTap(): void {
    const perch = this.perch();
    // Tucked: the tap means "come back out", not "open". One tap doing both
    // would make it impossible to retrieve Skipper without going somewhere.
    if (perch.tucked) {
      this.store.save({ ...perch, tucked: false });
      return;
    }
    this.greeting.set(null);
    void this.openStage();
  }

  /**
   * The flight belongs to the stage, not the perch: handing it this bird's
   * centre and size as the ORIGIN makes one continuous move from the edge to
   * centre stage. The perch is hidden for as long as the stage is open, so
   * there is never a second bird on screen.
   */
  private async openStage(): Promise<void> {
    const rect = this.avatarEl?.nativeElement.getBoundingClientRect();
    await this.stage.open({
      screenId: this.screen.screenId(),
      origin: rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null,
      originSize: rect?.height ?? null,
    });
    // The bird re-mounts once the stage releases its suppression; focus it
    // now if it already has, otherwise as soon as it does.
    if (this.birdEl?.isConnected) this.birdEl.focus({ preventScroll: true });
    else this.focusOnReturn = true;
  }

  // ── Keyboard: the web equivalent of the drag ───────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    const perch = this.perch();
    let next: SkipperPerch | null = null;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onTap();
        return;
      case 'ArrowUp':
      case 'ArrowDown': {
        const step = event.key === 'ArrowUp' ? -KEY_STEP : KEY_STEP;
        next = { ...perch, verticalFraction: Math.min(1, Math.max(0, perch.verticalFraction + step)) };
        break;
      }
      case 'ArrowLeft':
      case 'ArrowRight': {
        const toward = event.key === 'ArrowLeft' ? 'left' : 'right';
        if (perch.edge === toward) {
          next = { ...perch, tucked: true };
        } else if (perch.tucked) {
          next = { ...perch, tucked: false };
        } else {
          next = { ...perch, edge: toward };
        }
        break;
      }
      default:
        return;
    }
    event.preventDefault();
    this.dismissGreeting();
    this.store.save(next);
  }

  // ── Drag ────────────────────────────────────────────────────────────────────

  onPointerDown(event: PointerEvent): void {
    const el = this.birdEl;
    if (!el || event.button !== 0 || this.releaseDrag) return;

    this.viewport.measure();
    const bounds = this.bounds();
    const origin = this.settled();
    const start = { x: event.clientX, y: event.clientY };
    const pointerId = event.pointerId;
    let moved = false;
    let current: Point = origin;
    let samples: { t: number; x: number; y: number }[] = [];

    el.setPointerCapture(pointerId);

    this.zone.runOutsideAngular(() => {
      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        if (!moved) {
          if (Math.hypot(dx, dy) < COMPANION.dragSlop) return;
          moved = true;
          this.zone.run(() => {
            this.dismissGreeting();
            this.dragging.set(true);
          });
        }
        current = clampDrag({ x: origin.x + dx, y: origin.y + dy }, bounds);
        this.place(current, false);
        // Raw pointer positions: the clamp would read a push past the edge as a stop.
        samples.push({ t: ev.timeStamp, x: ev.clientX, y: ev.clientY });
        samples = samples.filter((s) => ev.timeStamp - s.t <= VELOCITY_WINDOW_MS);
      };

      const onEnd = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        cleanup();
        this.zone.run(() => {
          if (!moved) {
            if (ev.type === 'pointerup') this.onTap();
            return;
          }
          if (ev.type === 'pointerup') {
            samples.push({ t: ev.timeStamp, x: ev.clientX, y: ev.clientY });
            const velocity = velocityOf(samples, ev.timeStamp);
            this.store.save(resolveRelease(current, origin, velocity, bounds));
          }
          this.dragging.set(false);
        });
      };

      const cleanup = () => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onEnd);
        el.removeEventListener('pointercancel', onEnd);
        if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
        this.releaseDrag = null;
      };

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onEnd);
      el.addEventListener('pointercancel', onEnd);
      this.releaseDrag = cleanup;
    });
  }

  // ── Greeting ────────────────────────────────────────────────────────────────

  private armGreeting(): void {
    if (this.greetingArmed) return;
    this.greetingArmed = true;
    this.greetingTimer = setTimeout(() => {
      this.greetingArmed = false;
      if (!this.shown() || this.perch().tucked || this.dragging()) return;
      if (!this.memory.claimGreeting()) return;
      this.greeting.set(GREETINGS[Math.floor(Math.random() * GREETINGS.length)]);
      this.greetingTimer = setTimeout(() => this.greeting.set(null), GREETING_HOLD_MS);
    }, GREETING_DELAY_MS);
  }

  private dismissGreeting(): void {
    if (this.greeting()) {
      clearTimeout(this.greetingTimer);
      this.greeting.set(null);
    }
  }

  // ── Placement ───────────────────────────────────────────────────────────────

  /** Writes the box position directly. Settles over 260ms with a slight overshoot; tracks a drag exactly. */
  private place(at: Point, animate: boolean): void {
    const el = this.birdEl;
    if (!el) return;
    el.classList.toggle('settling', animate);
    el.style.transform = `translate3d(${at.x}px, ${at.y}px, 0)`;
  }

  private remeasureNextFrame(): void {
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.viewport.measure()));
  }
}

/**
 * Pointer velocity in px/s over the last 100ms BEFORE RELEASE. A pointer that
 * stopped and then let go has no recent samples and reads as still — so
 * placing Skipper carefully next to an edge is never mistaken for a flick.
 */
function velocityOf(samples: { t: number; x: number; y: number }[], releasedAt: number): Point {
  const recent = samples.filter((s) => releasedAt - s.t <= VELOCITY_WINDOW_MS);
  if (recent.length < 2) return { x: 0, y: 0 };
  const first = recent[0];
  const last = recent[recent.length - 1];
  const dt = last.t - first.t;
  if (dt <= 0) return { x: 0, y: 0 };
  return { x: ((last.x - first.x) / dt) * 1000, y: ((last.y - first.y) / dt) * 1000 };
}
